import React, { useEffect, useRef, useState } from "react"
import { CgSearch } from "react-icons/cg"
import { IoIosListBox, IoMdPhotos } from "react-icons/io"
import { MdOutlineRsvp } from "react-icons/md"
import { PiLinkSimpleBold } from "react-icons/pi"
import { RiSearchLine } from "react-icons/ri"
import { useNavigate, useParams } from "react-router-dom"

import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { EFeatureFlags, EVENT_STATUS } from "@letshang/constants"
import { useRecoilState, useRecoilValue } from "recoil"

import {
  filteredIntegrationsState,
  Integration,
  integrationsLoadingState,
  integrationsSearchQueryState,
  integrationsState,
} from "@fuse/recoil/integrations"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"

import { createEventWeb } from "@api/firebaseWrappedApis"

import { isDev } from "@fuse/utils/DeviceDection"

import List from "@fuse/assets/icons/list.svg"
import Mic from "@fuse/assets/icons/people_mic.svg"
import Photos from "@fuse/assets/icons/photos.svg"
import IconPuzzle from "@fuse/assets/icons/plugin/icon-puzzle.svg"
import Speaker from "@fuse/assets/icons/speaker.svg"

import EventPluginBottomSheet, {
  EventPluginBottomSheetHandle,
} from "./eventPluginsBottomSheet"
import EventPluginSkeleton from "./eventPluginSkeleton"

type CategoryTab = "all" | "visual" | "hosting_tools" | "experiences"

interface EventPluginSectionProps {
  eventId?: string
  onEventCreated?: (eventId: string) => void
  backgroundStyle?: {
    containerClassName?: string
    searchClassName?: string
  }
  bottomSheetRef?: React.RefObject<EventPluginBottomSheetHandle>
  user?: {
    isLoggedIn?: boolean
    [key: string]: any
  }
  onShowFastSignUp?: () => void
}

/**
 * Component for showing event integrations
 * This is a placeholder that can be expanded with actual integrations
 */
const EventAddAPluginSection: React.FC<EventPluginSectionProps> = ({
  eventId,
  onEventCreated,
  backgroundStyle,
  bottomSheetRef,
  user,
  onShowFastSignUp = () => {},
}) => {
  // All hooks must be called before any conditional returns
  const navigate = useNavigate()
  const params = useParams()
  const isIntegrationsEnabled =
    useProcessFeatureFlags(EFeatureFlags.ENABLE_ADD_PLUGIN_TO_EVENT) || isDev
  const [searchQuery, setSearchQuery] = useRecoilState(
    integrationsSearchQueryState,
  )
  const [filteredIntegrations, setFilteredIntegrations] = useRecoilState(
    filteredIntegrationsState,
  )
  const integrations = useRecoilValue(integrationsState)
  const [isLoading, setIsLoading] = useRecoilState(integrationsLoadingState)
  const [activeTab, setActiveTab] = useState<CategoryTab>("all")

  const currentEventId = eventId || params.eventId

  // Filter by category tab
  useEffect(() => {
    if (!isIntegrationsEnabled) return

    setIsLoading(true)
    setTimeout(() => {
      if (activeTab === "all") {
        setFilteredIntegrations(integrations)
      } else {
        let filtered: Integration[] = []

        if (activeTab === "visual") {
          filtered = integrations.filter((integration) =>
            ["shared_album", "shared_playlist"].includes(integration.category),
          )
        } else if (activeTab === "hosting_tools") {
          filtered = integrations.filter((integration) =>
            ["questionnaires"].includes(integration.category),
          )
        } else if (activeTab === "experiences") {
          filtered = integrations.filter((integration) =>
            ["rsvp_notes"].includes(integration.category),
          )
        }

        setFilteredIntegrations(filtered)
      }
      setIsLoading(false)
    }, 300)
  }, [
    activeTab,
    integrations,
    setFilteredIntegrations,
    setIsLoading,
    isIntegrationsEnabled,
  ])

  // If feature flag is disabled, don't render anything
  if (!isIntegrationsEnabled) {
    return null
  }

  // Open the bottom sheet when clicked
  const handleOpenBottomSheet = () => {
    // Check if user is authenticated
    if (!user?.isLoggedIn) {
      onShowFastSignUp?.()
      return
    }

    // Open modal immediately
    bottomSheetRef?.current?.openModal()
  }

  // Integration icon from Figma
  const renderIcon = () => <img src={IconPuzzle} alt="IconPuzzle" />

  // Default styling
  const defaultContainerClass =
    "mt-6 sm:mt-8 bg-white/10 border-white/20 border p-4 rounded-2xl w-full cursor-pointer"
  const defaultSearchClass =
    "flex gap-2 items-center justify-center bg-white/10 rounded-lg p-3 w-full"

  return (
    <div
      className={backgroundStyle?.containerClassName || defaultContainerClass}
      onClick={handleOpenBottomSheet}
    >
      <div className="flex flex-col w-full h-60">
        <div className="flex-1 flex flex-col justify-center items-center relative">
          <PiLinkSimpleBold className="text-white/50 font-semibold absolute top-[10%] rotate-12 right-[20%] text-[32px] lg:text-[44px]" />
          <img
            src={Photos}
            alt="Photos"
            className="text-white/50 font-semibold absolute top-[35%] right-[5%] h-8 w-8 lg:h-11 lg:w-11"
          />
          <MdOutlineRsvp className="text-white/50 font-semibold absolute top-[60%] rotate-12 right-[15%] text-[44px] lg:text-[60px]" />

          <img
            src={List}
            alt="ListBox"
            className="text-white/50 font-semibold absolute top-[10%] left-[20%] h-8 w-8 lg:h-11 lg:w-11"
          />

          <img
            src={Speaker}
            alt="SpeakerPhone"
            className="text-white/50 font-semibold absolute top-[35%] left-[5%] h-8 w-8 lg:h-11 lg:w-11"
          />
          <img
            src={Mic}
            alt="Conference"
            className="text-white/50 font-semibold absolute top-[60%] left-[15%] h-8 w-8 lg:h-11 lg:w-11"
          />
          <h3 className="text-lg lg:text-xl font-medium text-gray-300  text-center">
            More features
          </h3>
          <h3 className="text-lg lg:text-xl font-medium text-gray-300 mb-1 text-center">
            & settings
          </h3>
        </div>

        <div className={backgroundStyle?.searchClassName || defaultSearchClass}>
          <CgSearch size={20} color="#fff" />
          <p className="text-white text-md">Explore</p>
        </div>
      </div>
    </div>
  )
}

export default EventAddAPluginSection
